import { type ReactNode, useState } from 'react';
import { Box, Divider, Flex, Stack, Switch, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { firmDefaultTemplate } from 'src/data-capture/hooks/templates/defaultTemplate';
import type { TemplateWithoutSections } from 'src/data-capture/hooks/templates/useTemplates';
import { Button } from 'src/design-system/Button';
import { SearchableMenu, type SearchableMenuOption } from 'src/design-system/SearchableMenu';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { DefaultTemplateBadge } from 'src/meeting-note/components/default-template/DefaultTemplateBadge';
import { DefaultTemplateMenuFooter } from 'src/meeting-note/components/default-template/DefaultTemplateMenuFooter';
import { MakeDefaultAction } from 'src/meeting-note/components/default-template/MakeDefaultAction';
import { MakeDefaultTemplateModal } from 'src/meeting-note/components/default-template/MakeDefaultTemplateModal';
import type { AgendaOption } from 'src/meetings/hooks/useAgendaOptions';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Props = {
  templates: TemplateWithoutSections[];
  defaultTemplateId?: string;
  agendaOptions: AgendaOption[];
  defaultAgendaOptionId?: string;
  isGenerating: boolean;
  onGenerateTemplate: (templateId: string, summarise: boolean) => void;
  onGenerateAgenda: (agenda: AgendaOption, summarise: boolean) => void;
};

const switchStyles = { track: { width: 28, minWidth: 28, height: 16 } };

export const StructureSelection = ({
  templates,
  defaultTemplateId,
  agendaOptions,
  defaultAgendaOptionId,
  isGenerating,
  onGenerateTemplate,
  onGenerateAgenda,
}: Props) => {
  const [templateId, setTemplateId] = useState(defaultTemplateId ?? templates[0]?.id);
  const [templateSummarise, setTemplateSummarise] = useState(true);
  const [agendaOptionId, setAgendaOptionId] = useState(
    defaultAgendaOptionId ?? agendaOptions[0]?.id,
  );
  const [agendaSummarise, setAgendaSummarise] = useState(true);
  const [generatingOption, setGeneratingOption] = useState<'template' | 'agenda'>();
  const [templateToMakeDefault, setTemplateToMakeDefault] = useState<TemplateWithoutSections>();
  const { flagEnabledDefaultTemplates } = useFlags();

  const selectedTemplate = templates.find(template => template.id === templateId);
  const selectedAgenda = agendaOptions.find(agenda => agenda.id === agendaOptionId);
  const firmDefaultId = flagEnabledDefaultTemplates
    ? firmDefaultTemplate(templates)?.id
    : undefined;

  const templateOptions: SearchableMenuOption[] = templates.map(template => ({
    id: template.id,
    label: template.displayName,
    rightSection: flagEnabledDefaultTemplates
      ? templateOptionRightSection(template, firmDefaultId, () =>
          setTemplateToMakeDefault(template),
        )
      : undefined,
  }));

  return (
    <Stack gap={0}>
      <Text size="lg" fw={FONT_WEIGHTS.semibold} c="black">
        How would you like to structure the meeting note?
      </Text>

      <Stack gap="xs" mt="sm">
        <OptionBox
          title="Use a template"
          subtitle="Generate meeting note using a predefined template"
          isGenerating={isGenerating && generatingOption === 'template'}
          generateDisabled={!templateId || isGenerating}
          onGenerate={() => {
            if (!templateId) return;
            setGeneratingOption('template');
            onGenerateTemplate(templateId, templateSummarise);
          }}
          summarise={templateSummarise}
          onSummariseChange={setTemplateSummarise}
          dropdownLabel="Template"
          dropdownValue={selectedTemplate?.displayName}
          dropdownValueBadge={
            selectedTemplate && selectedTemplate.id === firmDefaultId ? (
              <DefaultTemplateBadge />
            ) : undefined
          }
          options={templateOptions}
          onSelectOption={setTemplateId}
          menuWidth={flagEnabledDefaultTemplates ? 320 : undefined}
          menuFooter={flagEnabledDefaultTemplates ? <DefaultTemplateMenuFooter /> : undefined}
        />

        <OptionBox
          title="Use an agenda"
          subtitle="Generate meeting note using an agenda file as the structure"
          isGenerating={isGenerating && generatingOption === 'agenda'}
          generateDisabled={!selectedAgenda || isGenerating}
          onGenerate={() => {
            if (!selectedAgenda) return;
            setGeneratingOption('agenda');
            onGenerateAgenda(selectedAgenda, agendaSummarise);
          }}
          summarise={agendaSummarise}
          onSummariseChange={setAgendaSummarise}
          dropdownLabel="Agenda"
          dropdownValue={selectedAgenda?.label}
          options={agendaOptions.map(agenda => ({ id: agenda.id, label: agenda.label }))}
          onSelectOption={setAgendaOptionId}
        />
      </Stack>

      {flagEnabledDefaultTemplates && (
        <MakeDefaultTemplateModal
          template={templateToMakeDefault}
          onClose={() => setTemplateToMakeDefault(undefined)}
        />
      )}
    </Stack>
  );
};

function templateOptionRightSection(
  template: TemplateWithoutSections,
  firmDefaultId: string | undefined,
  onMakeDefault: () => void,
): ReactNode {
  if (template.id === firmDefaultId) return <DefaultTemplateBadge />;
  if (template.isActive) return <MakeDefaultAction onClick={onMakeDefault} />;
  return undefined;
}

type OptionBoxProps = {
  title: string;
  subtitle: string;
  isGenerating: boolean;
  generateDisabled: boolean;
  onGenerate: () => void;
  summarise: boolean;
  onSummariseChange: (value: boolean) => void;
  dropdownLabel: string;
  dropdownValue?: string;
  dropdownValueBadge?: ReactNode;
  options: SearchableMenuOption[];
  onSelectOption: (id: string) => void;
  menuWidth?: number;
  menuFooter?: ReactNode;
};

function OptionBox({
  title,
  subtitle,
  isGenerating,
  generateDisabled,
  onGenerate,
  summarise,
  onSummariseChange,
  dropdownLabel,
  dropdownValue,
  dropdownValueBadge,
  options,
  onSelectOption,
  menuWidth = 260,
  menuFooter,
}: OptionBoxProps) {
  const optionNoun = dropdownLabel.toLowerCase();

  return (
    <Box bg="gray.0" p="sm" style={{ borderRadius: 12 }}>
      <Flex justify="space-between" align="center" gap="sm">
        <Box style={{ minWidth: 0 }}>
          <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
            {title}
          </Text>
          <Text size="xs" fw={FONT_WEIGHTS.regular} c="gray.7" mt={2}>
            {subtitle}
          </Text>
        </Box>
        <Button
          variant="primary"
          size="sm"
          onClick={onGenerate}
          loading={isGenerating}
          disabled={generateDisabled}
          style={{ flexShrink: 0 }}
        >
          Generate
        </Button>
      </Flex>

      <Divider color="gray.1" mt="xxs" />

      <Flex gap="xxs" mt="xxs">
        <SelectorBox>
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
            Summarise meeting note
          </Text>
          <Switch
            checked={summarise}
            onChange={event => onSummariseChange(event.currentTarget.checked)}
            color="brand.6"
            styles={switchStyles}
          />
        </SelectorBox>

        <SelectorBox>
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
            {dropdownLabel}
          </Text>
          <SearchableMenu
            position="bottom-end"
            width={menuWidth}
            withinPortal
            optionNoun={optionNoun}
            options={options}
            onSelectOption={onSelectOption}
            footer={menuFooter}
            target={
              <Flex align="center" gap="xxxs" style={{ cursor: 'pointer', minWidth: 0 }}>
                <Text
                  size="sm"
                  fw={FONT_WEIGHTS.medium}
                  c="gray.7"
                  lineClamp={1}
                  style={{ maxWidth: 120 }}
                >
                  {dropdownValue ?? 'Select'}
                </Text>
                {dropdownValueBadge}
                <IconChevronDown
                  size={16}
                  color="var(--mantine-color-gray-10)"
                  style={{ flexShrink: 0 }}
                />
              </Flex>
            }
          />
        </SelectorBox>
      </Flex>
    </Box>
  );
}

function SelectorBox({ children }: { children: ReactNode }) {
  return (
    <Flex
      flex={1}
      justify="space-between"
      align="center"
      gap="sm"
      bg="white"
      px="sm"
      py="xs"
      style={{ borderRadius: 16, minWidth: 0 }}
    >
      {children}
    </Flex>
  );
}
